import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "reactstrap";
import { DASHBOARD_USER_ROLES, singularRoleLabels } from "../../../constants";
import {
  formatCentsToDollars,
  formatDate,
  formatPhoneNumber,
  getAbreviatedState,
} from "../../../utils/utils";
import SelectCommissionStructureModal from "../../Authentication/Profile/modals/SelectCommissionStructureModal";
import { getUserCustomPricing } from "../../../slices/admin/thunk";

const CardDetails = ({ userProfile, currentUser, onRefresh }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAdmin = currentUser.role === DASHBOARD_USER_ROLES.ADMIN;

  const showCommissionStructure = (currentUser.role === DASHBOARD_USER_ROLES.NATIONAL && !currentUser.hideCommissionStructure) || isAdmin;

  const [
    openModalEditCommissionStructure,
    setOpenModalEditCommissionStructure,
  ] = useState(false);

  // Custom pricing state
  const [customPricing, setCustomPricing] = useState(null);
  const [loadingCustomPricing, setLoadingCustomPricing] = useState(false);

  const toggleModalEditCommissionStructure = () =>
    setOpenModalEditCommissionStructure(!openModalEditCommissionStructure);

  // Fetch custom pricing data
  const fetchCustomPricing = async () => {
    if (!userProfile?.id || !isAdmin) {
      return;
    }

    try {
      setLoadingCustomPricing(true);
      const response = await dispatch(getUserCustomPricing({ userId: userProfile.id }));
      if (response.payload?.data) {
        setCustomPricing(response.payload.data);
      }
    } catch (error) {
      console.error("Error fetching custom pricing:", error);
      setCustomPricing(null);
    } finally {
      setLoadingCustomPricing(false);
    }
  };

  useEffect(() => {
    if (userProfile?.id && isAdmin) {
      fetchCustomPricing();
    }
  }, [userProfile?.id, isAdmin]);

  const renderMainInfo = () => {
    const shouldRenderCompanyName =
      DASHBOARD_USER_ROLES.BROKER !== userProfile.role;

    return (
      <div className="my-3">
        {shouldRenderCompanyName && (
          <h5 className="mb-0">{userProfile.companyName}</h5>
        )}

        {userProfile.companyAddress && (
          <h6 className="mb-0 text-muted fw-normal">
            {userProfile.companyAddress}
          </h6>
        )}
        <h6 className="text-muted fw-normal">
          {userProfile.city || userProfile.state || userProfile.zipCode ? (
            <>
              {userProfile.city ? `${userProfile.city}, ` : ""}
              {userProfile.state
                ? `${getAbreviatedState(userProfile.state)}`
                : ""}
              {userProfile.zipCode ? ` ${userProfile.zipCode}` : ""}
            </>
          ) : null}
        </h6>
      </div>
    );
  };

  const renderPersonalInfo = () => {
    return (
      <div className="my-3">
        <h5 className="">
          {userProfile.firstName} {userProfile.lastName}
        </h5>
        {userProfile.email && (
          <h6 className=" text-muted fw-normal">{userProfile.email}</h6>
        )}
        {userProfile.phoneNumber && (
          <h6 className="text-muted fw-normal">
            {formatPhoneNumber(userProfile.phoneNumber)}
          </h6>
        )}
      </div>
    );
  };

  const renderAdditionalInfo = () => {
    const roleLabel = singularRoleLabels[userProfile.role];
    const inviteCode = userProfile.inviteCode;

    return (
      <div className="my-3">
        <h5 className="">
          {inviteCode && (
            <>
              {roleLabel} Code: {inviteCode}
            </>
          )}
        </h5>
        {userProfile.createdAt && (
          <h6 className="text-muted  fw-normal">
            <span>Date Added On: {formatDate(userProfile.createdAt)}</span>
          </h6>
        )}
      </div>
    );
  };

  const renderUserRolesAboveInfo = () => {
    const { brokerInfo, gaInfo, nationalInfo, role } = userProfile;

    // Helper function to get navigation path for a role
    const getNavigationPath = (roleKey, roleInfo) => {
      if (!roleInfo?.id) return null;

      switch (roleKey) {
        case 'national':
          return `/national/${roleInfo.id}`;
        case 'ga':
          return `/ga/${roleInfo.id}`;
        case 'broker':
          return `/brokers/${roleInfo.id}`;
        default:
          return null;
      }
    };

    // Helper function to render a single role card
    const renderRoleCard = (roleInfo, roleKey, isClickable = false) => {
      if (!roleInfo) return null;

      const roleLabel = singularRoleLabels[roleKey];
      const fullName = `${roleInfo.firstName || ''} ${roleInfo.lastName || ''}`.trim();
      const code = roleInfo.code || roleInfo.inviteCode || '';
      const navigationPath = getNavigationPath(roleKey, roleInfo);

      const handleClick = () => {
        if (isClickable && navigationPath) {
          navigate(navigationPath);
        }
      };

      const cardStyle = isClickable && navigationPath ? {
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      } : {};

      const cardClassName = isClickable && navigationPath
        ? 'mb-2 p-2 rounded hover-bg-light'
        : 'mb-2';

      return (
        <div
          key={roleKey}
          className={cardClassName}
          style={cardStyle}
          onClick={handleClick}
          onMouseEnter={(e) => {
            if (isClickable && navigationPath) {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (isClickable && navigationPath) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          <h5 className="mb-1 d-flex align-items-center">
            {roleLabel}: {fullName}
            {isClickable && navigationPath && (
              <i className="ri-arrow-right-line ms-2 text-muted"></i>
            )}
          </h5>
          {code && (
            <h6 className="text-muted fw-normal">
              Code: {code}
            </h6>
          )}
        </div>
      );
    };

    // For admins: show all available hierarchy with clickable links
    if (isAdmin) {
      const hasAnyInfo = nationalInfo || gaInfo || brokerInfo;

      if (!hasAnyInfo) {
        return null;
      }

      return (
        <div>
          <h5 className="mb-3">Upline Hierarchy</h5>
          {nationalInfo && renderRoleCard(nationalInfo, 'national', true)}
          {gaInfo && renderRoleCard(gaInfo, 'ga', true)}
          {brokerInfo && renderRoleCard(brokerInfo, 'broker', true)}
          <hr />
        </div>
      );
    }

    // For non-admins: show only one level above (not clickable)
    let roleAboveInfo = null;
    let roleAboveKey = null;

    switch (role) {
      case DASHBOARD_USER_ROLES.EMPLOYER:
        roleAboveInfo = brokerInfo;
        roleAboveKey = 'broker';
        break;
      case DASHBOARD_USER_ROLES.BROKER:
        roleAboveInfo = gaInfo;
        roleAboveKey = 'ga';
        break;
      case DASHBOARD_USER_ROLES.GA:
        roleAboveInfo = nationalInfo;
        roleAboveKey = 'national';
        break;
      default:
        return null;
    }

    if (!roleAboveInfo) {
      return null;
    }

    return (
      <div>
        {renderRoleCard(roleAboveInfo, roleAboveKey, false)}
        <hr />
      </div>
    );
  };

  const renderNumberOfEntitiesBelow = () => {
    const entities = userProfile.entitiesBelow || {};
    const result = [];

    const parseResult = (r) =>
      r.map((text, index) => (
        <h6 className="mb-1 fw-semibold text-muted" key={index}>
          {text.split(":")[0]}:{" "}
          <span className="text-muted fw-semibold">{text.split(":")[1]}</span>
        </h6>
      ));

    // For all: employees
    result.push(`Number of Employees: ${entities.numberOfEmployees || 0}`);

    if (userProfile.role === DASHBOARD_USER_ROLES.EMPLOYER) {
      return (
        <>
          <h5>Number of Entities</h5>
          {parseResult(result)}
        </>
      );
    }

    // For brokers: employers
    result.push(`Number of Employers: ${entities.numberOfEmployers || 0}`);

    if (userProfile.role === DASHBOARD_USER_ROLES.BROKER) {
      return (
        <>
          <h5>Number of Entities</h5>
          {parseResult(result)}
        </>
      );
    }

    // For GAs: brokers
    result.push(`Number of Brokers: ${entities.numberOfBrokers || 0}`);

    if (userProfile.role === DASHBOARD_USER_ROLES.GA) {
      return (
        <>
          <h5>Number of Entities</h5>
          {parseResult(result)}
        </>
      );
    }

    // For National: GAs
    result.push(`Number of GAs: ${entities.numberOfGAs || 0}`);

    return (
      <>
        <h5>Number of Entities</h5>
        {parseResult(result)}
      </>
    );
  };

  // const renderCustomEmployeeCommissions = () => {
  //   const customEmployeeCommissions = userProfile.customEmployeeCommissions;

  //   if (!customEmployeeCommissions) {
  //     return null;
  //   }

  //   // Convert cents to dollars and put $ sign
  //   const parseCommission = (commission) => `$${(commission / 100).toFixed(2)}`;

  //   return (
  //     <div>
  //       <h5>Custom Employee Commissions</h5>
  //       <h6 className="text-muted fw-semibold">
  //         {Object.keys(customEmployeeCommissions).map((role, index) => (
  //           <span className="" key={index}>
  //             {singularRoleLabels[role]}:{" "}
  //             <span className="text-muted fw-semibold  ">
  //               {parseCommission(customEmployeeCommissions[role])}
  //             </span>
  //             {index < Object.keys(customEmployeeCommissions).length - 1 && (
  //               <div className="d-flex my-1"></div>
  //             )}
  //           </span>
  //         ))}
  //       </h6>
  //     </div>
  //   );
  // };

  const renderCommissionStructureBreakdown = (structure) => {

    // If it's a national adding a broker directly, remove GA commission and make national commission as national + ga.

    let nationalNumber = structure?.national;
    let gaNumber = structure?.ga;
    let brokerNumber = structure?.broker;

    const profileUserRole = userProfile.role;

    if (profileUserRole === DASHBOARD_USER_ROLES.BROKER && currentUser.role === DASHBOARD_USER_ROLES.NATIONAL
      && !userProfile.gaId
    ) {
      nationalNumber = nationalNumber + gaNumber;
      gaNumber = 0;
    }




    return (<div>
      <div>
        National:{" "}
        <strong>
          ${formatCentsToDollars(nationalNumber)}
        </strong>
      </div>
      {gaNumber ? (
        <div>
          GA:{" "}
          <strong>
            ${formatCentsToDollars(gaNumber)}
          </strong>
        </div>
      ) : null}

      <div>
        Broker:{" "}
        <strong>
          ${formatCentsToDollars(brokerNumber)}
        </strong>
      </div>
    </div>
    )

  }

  const renderCommissionStructure = () => {
    const commissionStructure = userProfile.commissionStructure;

    if (!commissionStructure) {
      return null;
    }


    // For brokers, only allow edit if the current user is national and they don't have a GA Id.
    const isUserBroker = userProfile.role === DASHBOARD_USER_ROLES.BROKER;
    const userHasGAId = userProfile.gaId;

    let allowEdit = currentUser.role === DASHBOARD_USER_ROLES.NATIONAL;

    if (isUserBroker && userHasGAId) {
      allowEdit = false;
    }



    // Convert cents to dollars and put $ sign
    const parseCommission = (commission) => `$${(commission / 100).toFixed(2)}`;

    return (
      <div>
        <SelectCommissionStructureModal
          open={openModalEditCommissionStructure}
          setOpen={setOpenModalEditCommissionStructure}
          currentUser={currentUser}
          userProfile={userProfile}
          onRefresh={onRefresh}
        />
        <div className="d-flex align-items-start justify-content-between">
          <h5>Commission Structure</h5>
          {currentUser.role === DASHBOARD_USER_ROLES.NATIONAL && allowEdit && (
            <div className="d-flex justify-content-end ">
              <Button
                onClick={() => toggleModalEditCommissionStructure()}
                className="btn btn-soft-primary border-primary p-0 px-1 m-0"
                size="xs"
              >
                <i className="bx bx-pencil m-0 p-1"></i>
              </Button>
            </div>
          )}
        </div>
        <h6 className="text-muted fw-semibold">
          <div className="mb-2">
            Structure Name:{" "}
            <span className="text-muted fw-semibold">
              {commissionStructure.name}
            </span>
          </div>

          {renderCommissionStructureBreakdown(commissionStructure)}

          {/* {["national", "ga", "broker"].map((role, index) => (
            <div key={index} className="my-1">
              {commissionStructure[role] !== undefined && (
                <>
                  {singularRoleLabels[role]}:{" "}
                  <span className="text-muted fw-semibold">
                    {parseCommission(commissionStructure[role])}
                  </span>
                </>
              )}
            </div>
          ))} */}
        </h6>
      </div>
    );
  };

  const renderCustomPricing = () => {
    // Only show for admins and if custom pricing exists
    if (!isAdmin || !customPricing?.hasCustomPricing) {
      return null;
    }

    const pricing = customPricing.customPricing;

    return (
      <div>
        <h5>Custom Pricing</h5>
        <h6 className="text-muted fw-semibold">
          <div className="mb-1">
            Individual Plan:{" "}
            <span className="text-muted fw-semibold">
              {pricing.individualPlanAmount} cents (${formatCentsToDollars(pricing.individualPlanAmount)})
            </span>
          </div>
          <div className="mb-1">
            Family Plan:{" "}
            <span className="text-muted fw-semibold">
              {pricing.familyPlanAmount} cents (${formatCentsToDollars(pricing.familyPlanAmount)})
            </span>
          </div>
        </h6>
      </div>
    );
  };

  const renderManagedBillingText = () => {
    if (!userProfile.managedBilling) return;
    if (
      isAdmin &&
      (userProfile.role === DASHBOARD_USER_ROLES.NATIONAL ||
        userProfile.role === DASHBOARD_USER_ROLES.EMPLOYER)
    ) {
      return (
        <>
          {" "}
          <hr />
          <h5>Managed Billing</h5>
          <span className="text-muted fw-normal">
            {userProfile.role === DASHBOARD_USER_ROLES.NATIONAL
              ? "This national employers will not be billed for their employees."
              : "Employer will not be billed for their employees."}
          </span>
        </>
      );
    }

    return null;
  };

  return (
    <div className="pt-1 px-1">
      {renderPersonalInfo()}
      <hr />
      {renderMainInfo()}

      <hr />

      {renderAdditionalInfo()}
      <hr />

      {renderUserRolesAboveInfo()}

      {renderNumberOfEntitiesBelow()}

      {/* {userProfile.role === DASHBOARD_USER_ROLES.NATIONAL && isAdmin && (
        <>
          <hr />
          {renderCustomEmployeeCommissions()}
        </>
      )} */}

      {showCommissionStructure && (userProfile.role === DASHBOARD_USER_ROLES.NATIONAL ||
        userProfile.role === DASHBOARD_USER_ROLES.GA ||
        userProfile.role === DASHBOARD_USER_ROLES.BROKER) && (
          <>
            <hr />
            {renderCommissionStructure()}
          </>
        )}

      {customPricing?.hasCustomPricing && (
        <>
          <hr />
          {renderCustomPricing()}
        </>
      )}

      {renderManagedBillingText()}
    </div>
  );
};

export default CardDetails;
